import { cloneAuthoring, compareNumbers, stringifyAuthoringJSON } from "./authoring-json.mjs";
import { dependencies as nodeDependencies, editableSubject } from "./authoring-subject.mjs";
import { routeHash } from "./helpers.mjs";

export const canAuthor = (session) => !(session?.roles || []).some((role) => ["platform_admin", "break_glass_admin"].includes(role)) && (session?.roles || []).some((role) => ["organization_admin", "project_admin"].includes(role));
export const canAuthorPlatform = (session) => session?.principal?.type === "user" && session?.scope?.organizationId === "aiharness" && session?.roles?.includes("platform_admin") === true;
export const authoringLocation = (session, site = "ru") => ({ section: ["triproutes", "corplinks"].includes(session?.scope?.organizationId) ? session.scope.organizationId : "workspace", ...(session?.scope?.organizationId === "triproutes" ? { site } : {}) });
export function authoringHref(kind = "pipeline", id = "", location = {}) {
  if (location.catalog === "platform") return routeHash("settings", "ready-solutions", { tab: id ? "draft" : kind === "agent" ? "agents" : "solutions", ...(id ? { resource: id, kind } : {}) });
  if (kind === "pipeline" && location.section === "triproutes") return routeHash("settings", "triproutes-solutions", {site: location.site === "app" ? "app" : "ru", tab: id ? "draft" : "solutions", ...(id ? {resource:id,kind} : {})});
  return routeHash(location.section || "workspace", kind === "agent" ? "agents" : "solutions", { ...(location.site ? { site: location.site } : {}), ...(id ? { resource: id, kind } : {}) });
}
export function authoringAPI(api, location) {
  return { request(path, options) {
    const [pathname, query = ""] = path.split("?");
    const params = new URLSearchParams(query);
    if (location.site) params.set("site", location.site);
    const target = location.catalog === "platform" ? pathname.replace(/^\/v1\/authoring\/(?!platform\/)/, "/v1/authoring/platform/") : pathname;
    return api.request(`${target}${params.size ? `?${params}` : ""}`, options);
  } };
}
export const nodeLabel = (node) => node.name || ({ input: "Вход", result: "Результат", ai: "AI-шаг" }[node.type]) || node.id;
export const emptySchema = (type = "object") => type === "object" ? { type, properties: {}, required: [], additionalProperties: false } : type === "array" ? { type, items: { type: "string" } } : { type };
export const initialLayout = () => ({ nodes: {}, viewport: { x: 30, y: 70, zoom: 1 } });

// React Flow clears its internal measurements when it receives fresh node
// objects without `measured`. Keep ResizeObserver results in view state, outside
// the saved document/layout, when rebuilding nodes for selection or editing.
export function retainNodeMeasurements(previous, changes) {
  let next = previous;
  for (const change of changes) {
    if (change.type !== "dimensions" || !change.dimensions) continue;
    const { width, height } = change.dimensions;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) continue;
    if (next[change.id]?.width === width && next[change.id]?.height === height) continue;
    next = { ...next, [change.id]: { width, height } };
  }
  return next;
}

export function arrangedPositions(document) {
  const ranks = new Map(), lanes = new Map();
  function rank(id, visiting = new Set()) {
    if (ranks.has(id)) return ranks.get(id);
    if (visiting.has(id)) return 0;
    visiting.add(id);
    const dependencies = nodeDependencies(document.nodes.find((node) => node.id === id));
    const value = dependencies.length ? 1 + Math.max(...dependencies.map((dep) => rank(dep, new Set(visiting)))) : 0;
    ranks.set(id, value); return value;
  }
  return Object.fromEntries(document.nodes.map((node) => {
    const column = rank(node.id), row = lanes.get(column) || 0;
    lanes.set(column, row + 1);
    return [node.id, { x: column * 280, y: row * 190 + 70 }];
  }));
}
const textSchema = () => ({ ...emptySchema(), properties: { text: { type: "string", maxLength: 4000 } }, required: ["text"] });
export function newDocument(kind = "pipeline") {
  const document = { authoringVersion: "1.0", kind, name: kind === "agent" ? "Новый агент" : "Новое решение", description: "",
    inputSchema: textSchema(), inputForm: { text: { label: "Текст", multiline: true } }, schemas: { result: textSchema() },
    nodes: [{ id: "input", type: "input", dependsOn: [] }, { id: "result", type: "result", dependsOn: ["input"] }],
    resultSchemaKey: "result", limits: { maxDurationSeconds: 300, maxConcurrentStages: 2 }, dataClassification: "public" };
  if (kind === "agent") { delete document.inputForm; return addAI(document, "input", "result").document; }
  return document;
}
export function addAI(source, predecessor = "input", successor = "") {
  if (source.nodes.length >= 50 || source.kind === "agent" && source.nodes.some((node) => node.type === "ai")) throw new Error("Достигнут предел шагов");
  const document = cloneAuthoring(source);
  let index = 1;
  while (document.nodes.some((node) => node.id === `ai_${index}`) || Object.hasOwn(document.schemas, `ai_${index}_input`) || Object.hasOwn(document.schemas, `ai_${index}_output`)) index++;
  const id = `ai_${index}`;
  const parent = document.nodes.find((node) => node.id === predecessor && node.type !== "result");
  if (!parent) throw new Error("Выберите предшествующий шаг");
  const schema = parent.type === "input" ? document.inputSchema : document.schemas[parent.outputSchemaKey];
  document.schemas[`${id}_input`] = cloneAuthoring(schema || emptySchema());
  document.schemas[`${id}_output`] = textSchema();
  document.nodes.splice(document.nodes.length - 1, 0, { id, type: "ai", name: `AI-шаг ${index}`, dependsOn: [predecessor],
    instruction: "Обработай входные данные и верни результат по выходной схеме.", modelChoiceRef: "unresolved",
    inputSchemaKey: `${id}_input`, outputSchemaKey: `${id}_output`, maxOutputTokens: 1000 });
  if (successor) document.nodes.find((node) => node.id === successor).dependsOn = document.nodes.find((node) => node.id === successor).dependsOn.map((dep) => dep === predecessor ? id : dep);
  return { document, id };
}
export function connectNodes(source, from, to, replaced = null) {
  const document = cloneAuthoring(source);
  const origin = document.nodes.find((node) => node.id === from), target = document.nodes.find((node) => node.id === to);
  if (!origin || !target || from === to || origin.type === "result" || target.type === "input") throw new Error("Эта связь недопустима");
  if (replaced) document.nodes.find((node) => node.id === replaced.target).dependsOn = document.nodes.find((node) => node.id === replaced.target).dependsOn.filter((id) => id !== replaced.source);
  if (target.dependsOn.includes(from)) throw new Error("Такая связь уже есть");
  const ancestors = (id, seen = new Set()) => {
    if (id === to) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return (nodeDependencies(document.nodes.find((node) => node.id === id))).some((dep) => ancestors(dep, seen));
  };
  if (ancestors(from)) throw new Error("Связь создаёт цикл");
  if (document.nodes.reduce((count, node) => count + (node.dependsOn || []).length, 0) >= 200) throw new Error("Не больше 200 связей");
  target.dependsOn.push(from);
  return document;
}
export function removeNode(source, id) {
  const node = source.nodes.find((item) => item.id === id);
  if (!node || ["input", "result"].includes(node.type)) throw new Error("Вход и результат нельзя удалить");
  const document = cloneAuthoring(source);
  document.nodes = document.nodes.filter((item) => item.id !== id).map((item) => ({ ...item, dependsOn: (item.dependsOn || []).filter((dep) => dep !== id) }));
  return document;
}
export function duplicateNode(source, id) {
  const original = source.nodes.find((node) => node.id === id);
  if (original?.type !== "ai" || original.sourceAgentVersionRef) throw new Error("Копировать можно локальный AI-шаг");
  const copy = addAI(source, original.dependsOn[0] || "input");
  copy.document.nodes = copy.document.nodes.map((node) => node.id !== copy.id ? node : {
    ...cloneAuthoring(original), id: copy.id, name: `${nodeLabel(original).slice(0, 110)} (копия)`, inputSchemaKey: node.inputSchemaKey, outputSchemaKey: node.outputSchemaKey,
  });
  const node = copy.document.nodes.find((item) => item.id === copy.id);
  copy.document.schemas[node.inputSchemaKey] = cloneAuthoring(source.schemas[original.inputSchemaKey]);
  copy.document.schemas[node.outputSchemaKey] = cloneAuthoring(source.schemas[original.outputSchemaKey]);
  return copy;
}
export function editableDocument(document, context) {
  if (editableSubject(document,context)) return true;
  return Boolean(document?.authoringVersion === "1.0" && document.inputSchema && !Object.keys(document).some((key) => key.endsWith("Ref")) &&
    document.nodes.every((node) => ["input", "ai", "result"].includes(node.type) && !node.sourceAgentVersionRef) &&
    Object.values(document.schemas).every((schema) => schema.type === "object"));
}
export function localIssues(document) {
  const issues = [];
  const add = (message, nodeId = "", fieldPath = "") => issues.push({ message, nodeId, fieldPath });
  if (!document.name.trim()) add("Укажите название");
  const ids = new Set(document.nodes.map((node) => node.id));
  if (ids.size !== document.nodes.length) add("ID шагов должны быть уникальны");
  for (const type of ["input", "result"]) if (document.nodes.filter((node) => node.type === type).length !== 1) add("Нужны ровно один вход и один результат");
  const visited = new Set(), visiting = new Set();
  const checkCycle = (id) => {
    if (visiting.has(id)) { add("Граф содержит цикл", id, "dependsOn"); return; }
    if (visited.has(id)) return;
    visiting.add(id);
    (nodeDependencies(document.nodes.find((node) => node.id === id))).forEach(checkCycle);
    visiting.delete(id); visited.add(id);
  };
  document.nodes.forEach((node) => checkCycle(node.id));
  const result = document.nodes.find((node) => node.type === "result");
  const used = new Set();
  const visit = (id) => { if (used.has(id)) return; used.add(id); (nodeDependencies(document.nodes.find((node) => node.id === id))).forEach(visit); };
  if (result) visit(result.id);
  for (const node of document.nodes) {
    if (!used.has(node.id)) add("Шаг не связан с результатом", node.id);
    if (node.type !== "input" && !nodeDependencies(node).length) add("Подключите вход шага", node.id, "dependsOn");
    if (nodeDependencies(node).some((id) => !ids.has(id))) add("Источник связи отсутствует", node.id, "dependsOn");
    if (node.type === "ai") {
      if (!node.instruction?.trim()) add("Заполните инструкцию", node.id, "instruction");
      if (new TextEncoder().encode(node.instruction || "").length > 32768) add("Инструкция превышает 32 KiB", node.id, "instruction");
      if (!node.modelChoiceRef || node.modelChoiceRef === "unresolved") add("Модель не выбрана: каталог допущенных моделей ещё не подключён", node.id, "modelChoiceRef");
      if (!document.schemas[node.inputSchemaKey] || !document.schemas[node.outputSchemaKey]) add("Схема шага отсутствует", node.id);
    }
  }
  const checkSchema = (schema, owner, path, flat = false) => {
    if (!schema?.type) return;
    let properties = 0;
    const walk = (value, fieldPath, depth) => {
      if (depth > 6) { add("Глубина схемы превышает 6", owner, fieldPath); return; }
      for (const [min, max] of [["minimum", "maximum"], ["minLength", "maxLength"], ["minItems", "maxItems"]]) {
        if (value[min] != null && value[max] != null && compareNumbers(value[min], value[max]) > 0) add("Минимум превышает максимум", owner, fieldPath);
      }
      if (value.enum && new Set(value.enum.map(stringifyAuthoringJSON)).size !== value.enum.length) add("Варианты enum не должны повторяться", owner, fieldPath);
      if (value.type === "object") {
        properties += Object.keys(value.properties).length;
        for (const [key, child] of Object.entries(value.properties)) {
          if (flat && depth === 1 && ["object", "array"].includes(child.type)) add("Входная форма решения должна быть плоской", owner, `${fieldPath}/${key}`);
          walk(child, `${fieldPath}/${key}`, depth + 1);
        }
      }
      if (value.type === "array") walk(value.items, `${fieldPath}/items`, depth + 1);
    };
    walk(schema, path, 1);
    if (properties > 100) add("Схема содержит больше 100 свойств", owner, path);
    if (new TextEncoder().encode(stringifyAuthoringJSON(schema)).length > 128 * 1024) add("Схема превышает 128 KiB", owner, path);
  };
  checkSchema(document.inputSchema, "input", "inputSchema", document.kind === "pipeline");
  if (document.kind === "pipeline" && Object.keys(document.inputSchema?.properties || {}).length > 30) add("В форме больше 30 полей", "input");
  for (const [key, schema] of Object.entries(document.schemas)) {
    const owner = document.nodes.find((node) => node.inputSchemaKey === key || node.outputSchemaKey === key);
    checkSchema(schema, owner?.id || "result", `schemas/${key}`);
  }
  if (new TextEncoder().encode(stringifyAuthoringJSON(document)).length > 1024 * 1024) add("Черновик превышает 1 MiB");
  return issues;
}
export const sameContent = (a, b) => stringifyAuthoringJSON(a) === stringifyAuthoringJSON(b);

// Retain an uncertain request until its original response is recovered. A save
// of the other channel must never replace this channel's newer local data.
export function mutationQueue(api, request, keyFactory) {
  let pending = null;
  return {
    get pending() { return pending; },
    clear() { pending = null; },
    async run(path, body, headers = {}, method = "PUT") {
      pending ||= { path, body: cloneAuthoring(body), headers: { ...headers, "Idempotency-Key": keyFactory() }, method };
      try {
        const result = await request(api, pending.path, pending);
        pending = null;
        return result;
      } catch (error) {
        if (error.status >= 400 && error.status < 500) pending = null;
        throw error;
      }
    },
  };
}
